(() => {
  const drawer = document.querySelector('[data-wishlist-drawer]');
  if (!drawer) return;

  const storageKey = 'm-wishlist-products';
  const itemsContainer = drawer.querySelector('[data-wishlist-drawer-items]');
  const emptyState = drawer.querySelector('[data-wishlist-drawer-empty]');
  const footer = drawer.querySelector('[data-wishlist-drawer-footer]');
  const loading = drawer.querySelector('[data-wishlist-drawer-loading]');
  const panel = drawer.querySelector('.m-wishlist-drawer__panel');
  const closeButton = drawer.querySelector('.m-wishlist-drawer__close');
  const clearAllButton = drawer.querySelector('[data-wishlist-drawer-clear-all]');
  let lastFocusedElement = null;
  let renderId = 0;

  const readProducts = () => {
    try {
      const value = JSON.parse(localStorage.getItem(storageKey) || '[]');
      return Array.from(new Set(Array.isArray(value) ? value.filter(Boolean) : []));
    } catch (error) {
      return [];
    }
  };

  const writeProducts = (products) => {
    localStorage.setItem(storageKey, JSON.stringify(Array.from(new Set(products))));
  };

  const updateCounts = (products = readProducts()) => {
    const count = products.length;
    document
      .querySelectorAll('.m-wishlist-count:not(.ssw-counter-fave-menu)')
      .forEach((counter) => {
        counter.textContent = count;
        counter.classList.toggle('m:hidden', count < 1);
      });
    document.body.classList.toggle('wishlist-has-item', count > 0);
    if (clearAllButton) clearAllButton.hidden = count < 1;
  };

  const productUrl = (handle) => {
    if (typeof window.formatUrl === 'function') {
      return window.formatUrl('products', handle, 'view=grid-card-item');
    }
    const root = window.Shopify?.routes?.root || '/';
    return `${root}products/${encodeURIComponent(handle)}?view=grid-card-item`;
  };

  const createRemoveButton = (handle) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'm-wishlist-drawer__remove';
    button.dataset.wishlistDrawerRemove = handle;
    button.setAttribute('aria-label', 'إزالة من المفضلة');
    button.innerHTML =
      '<svg aria-hidden="true" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M6.5 6.5l11 11M17.5 6.5l-11 11"/></svg>';
    return button;
  };

  const fetchProduct = async (handle) => {
    const response = await fetch(productUrl(handle), {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    if (!response.ok) throw new Error(`Wishlist product request failed: ${response.status}`);

    const item = document.createElement('article');
    item.className = 'm-wishlist-drawer__item';
    item.dataset.productHandle = handle;
    item.innerHTML = await response.text();

    if (!item.querySelector('.m-product-card')) {
      throw new Error('Wishlist product card was not found');
    }

    item.appendChild(createRemoveButton(handle));
    return item;
  };

  const render = async () => {
    const currentRenderId = ++renderId;
    const products = readProducts();
    updateCounts(products);
    itemsContainer.replaceChildren();

    if (!products.length) {
      loading.setAttribute('aria-hidden', 'true');
      emptyState.hidden = false;
      footer.hidden = true;
      return;
    }

    emptyState.hidden = true;
    footer.hidden = false;
    loading.setAttribute('aria-hidden', 'false');

    const results = await Promise.allSettled(products.map(fetchProduct));
    if (currentRenderId !== renderId) return;

    results.forEach((result) => {
      if (result.status === 'fulfilled') itemsContainer.appendChild(result.value);
    });

    loading.setAttribute('aria-hidden', 'true');
    if (!itemsContainer.children.length) {
      emptyState.hidden = false;
      footer.hidden = true;
    }

    window.MinimogTheme?.Wishlist?.setWishlistButtonsState?.();
  };

  const open = (trigger) => {
    lastFocusedElement = trigger;
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('m-wishlist-drawer-open');
    requestAnimationFrame(() => drawer.classList.add('is-open'));
    render();
    window.setTimeout(() => closeButton?.focus(), 80);
  };

  const close = ({ immediate = false, restoreFocus = true } = {}) => {
    drawer.classList.remove('is-open');
    document.body.classList.remove('m-wishlist-drawer-open');

    if (immediate) {
      drawer.setAttribute('aria-hidden', 'true');
      if (restoreFocus) lastFocusedElement?.focus?.();
      return;
    }

    window.setTimeout(() => {
      drawer.setAttribute('aria-hidden', 'true');
      if (restoreFocus) lastFocusedElement?.focus?.();
    }, 280);
  };

  document.addEventListener('click', (event) => {
    /*
      Cart has priority over Wishlist:
      when Add to cart is clicked from a wishlist product card, begin closing
      the wishlist immediately but DO NOT prevent/stop the original product-form
      click. The theme keeps handling the actual add-to-cart request.
    */
    const addToCart = event.target.closest('.m-wishlist-drawer .m-add-to-cart');
    if (addToCart) {
      close({ immediate: false, restoreFocus: false });
    }

    const trigger = event.target.closest('[data-wishlist-drawer-trigger]');
    if (trigger) {
      event.preventDefault();
      open(trigger);
      return;
    }

    if (event.target.closest('[data-wishlist-drawer-close]')) {
      close();
      return;
    }

    const clearAll = event.target.closest('[data-wishlist-drawer-clear-all]');
    if (clearAll) {
      event.preventDefault();
      const products = readProducts();
      const wishlist = window.MinimogTheme?.Wishlist;
      if (!products.length) {
        updateCounts([]);
        emptyState.hidden = false;
        footer.hidden = true;
        itemsContainer.replaceChildren();
        if (wishlist) {
          wishlist.products = [];
          wishlist.saveToStorage?.();
          wishlist.syncWishlistButtons?.();
        }
        return;
      }

      clearAll.disabled = true;
      const label = clearAll.querySelector('[data-wishlist-clear-label]');
      const originalLabel = label ? label.textContent : 'حذف الكل';
      if (label) label.textContent = 'جارٍ الحذف...';

      try {
        // Keep the theme Wishlist controller in sync with localStorage.
        if (wishlist) {
          products.forEach((handle) => wishlist.removeFromWishlist?.(handle));
        }

        localStorage.removeItem(storageKey);
        localStorage.removeItem('wishlist-prices');

        updateCounts([]);
        itemsContainer.replaceChildren();
        emptyState.hidden = false;
        footer.hidden = true;

        // Make every visible wishlist button reflect the cleared state.
        wishlist?.syncWishlistButtons?.();
        wishlist?.showWishlistFeedback?.('remove');
      } catch (error) {
        console.error('[Wishlist] Failed to clear all items:', error);
      } finally {
        clearAll.disabled = false;
        if (label) label.textContent = originalLabel;
        updateCounts();
      }
      return;
    }

    const removeButton = event.target.closest('[data-wishlist-drawer-remove]');
    if (removeButton) {
      const handle = removeButton.dataset.wishlistDrawerRemove;
      const products = readProducts().filter((product) => product !== handle);
      writeProducts(products);
      updateCounts(products);
      removeButton.closest('.m-wishlist-drawer__item')?.remove();
      const wishlist = window.MinimogTheme?.Wishlist;
      wishlist?.removeFromWishlist?.(handle);
      wishlist?.syncWishlistButtons?.();
      wishlist?.showWishlistFeedback?.('remove');

      if (!products.length) {
        emptyState.hidden = false;
        footer.hidden = true;
      }
      return;
    }

    if (event.target.closest('.m-wishlist-button')) {
      window.setTimeout(() => {
        updateCounts();
        if (drawer.getAttribute('aria-hidden') === 'false') render();
      }, 0);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (drawer.getAttribute('aria-hidden') !== 'false') return;

    if (event.key === 'Escape') {
      close();
      return;
    }

    if (event.key === 'Tab') {
      const focusable = Array.from(
        panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')
      ).filter((element) => element.offsetParent !== null);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  });

  window.addEventListener('storage', (event) => {
    if (event.key !== storageKey) return;
    updateCounts();
    if (drawer.getAttribute('aria-hidden') === 'false') render();
  });

  /*
    Hard safety layer:
    the theme cart drawer uses a lower z-index than the wishlist drawer.
    If the cart becomes active while wishlist is still transitioning,
    instantly hide wishlist so the cart is always the top/visible drawer.
  */
  const cartDrawer = document.querySelector('m-cart-drawer, .m-cart-drawer');

  if (cartDrawer) {
    const syncDrawerPriority = () => {
      const cartIsOpen =
        cartDrawer.classList.contains('m-cart-drawer--active') ||
        cartDrawer.getAttribute('aria-hidden') === 'false';

      if (cartIsOpen && drawer.getAttribute('aria-hidden') === 'false') {
        close({ immediate: true, restoreFocus: false });
      }
    };

    const cartObserver = new MutationObserver(syncDrawerPriority);
    cartObserver.observe(cartDrawer, {
      attributes: true,
      attributeFilter: ['class', 'aria-hidden']
    });

    syncDrawerPriority();
  }

  updateCounts();
})();